import React, { useState, useEffect } from "react";
import { Table, Typography, Card, Button, Spin } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  PrinterOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import Login from "../../Authentication/Login";
import AccontingReportTabs from "./AccoutingReportTabs";
import Header from "../../Common/Layout/Header";

const { Text, Title } = Typography;

const TrialBalanceReport = () => {
  const { user, loading } = useAuth();
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [companyData, setCompanyData] = useState(null);
  const [reportData, setReportData] = useState({
    accounts: [],
    totalDebit: 0,
    totalCredit: 0,
  });
  const [loadingReport, setLoadingReport] = useState(false);

  // Fetch company data
  const getCompanyData = async () => {
    try {
      const res = await apiService.get(`${config.GETCOMPANYBYUSER}/${user.id}`);
      setCompanyData(res);
    } catch (err) {
      console.error("Error fetching company data:", err);
    }
  };

  // Fetch trial balance data
  const getTrialBalance = async () => {
    setLoadingReport(true);
    try {
      const params = new URLSearchParams();
      if (fromDate)
        params.append("FromDate", moment(fromDate).format("YYYY-MM-DD"));
      if (toDate) params.append("ToDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.TRIALBALANCEREPORT}/${user.id}?${params.toString()}`
      );

      // Calculate totals
      const totalDebit = res.reduce(
        (sum, account) => sum + (account.debit || 0),
        0
      );
      const totalCredit = res.reduce(
        (sum, account) => sum + (account.credit || 0),
        0
      );

      setReportData({
        accounts: res || [],
        totalDebit,
        totalCredit,
      });
    } catch (err) {
      console.error("Error fetching trial balance:", err);
      setReportData({
        accounts: [],
        totalDebit: 0,
        totalCredit: 0,
      });
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      getCompanyData();
      getTrialBalance();
    }
  }, [user, loading]);

  const handleSearch = (e) => {
    e.preventDefault();
    getTrialBalance();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setReportData({
      accounts: [],
      totalDebit: 0,
      totalCredit: 0,
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set styles for the document
    doc.setFont("helvetica");
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.setFont(undefined, "bold");

    // Title - centered
    doc.text("TRIAL BALANCE", 105, 20, { align: "center" });

    // Company details - centered
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");
    doc.text(companyData?.companyName || "Company", 105, 30, {
      align: "center",
    });

    const addressLine = `${companyData?.address || ""}, ${
      companyData?.city || ""
    }`;
    doc.text(addressLine, 105, 35, { align: "center" });

    const contactLine = `Phone: ${companyData?.phoneNumber || ""}`;
    doc.text(contactLine, 105, 40, { align: "center" });

    // Report period
    doc.text(`As of ${moment(toDate).format("MMM DD, YYYY")}`, 105, 45, {
      align: "center",
    });

    // Generated date
    doc.text(
      `Generated on: ${moment().format("YYYY-MM-DD hh:mm A")}`,
      105,
      50,
      { align: "center" }
    );

    // Trial Balance table
    doc.autoTable({
      startY: 60,
      head: [["Account Code", "Account Name", "Debit", "Credit"]],
      body: reportData.accounts.map((account) => [
        account.accountCode,
        account.accountName,
        account.debit ? account.debit.toFixed(2) : "-",
        account.credit ? account.credit.toFixed(2) : "-",
      ]),
      foot: [
        [
          { content: "Totals:", styles: { fontStyle: "bold" } },
          "",
          {
            content: reportData.totalDebit.toFixed(2),
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: reportData.totalCredit.toFixed(2),
            styles: { fontStyle: "bold", halign: "right" },
          },
        ],
      ],
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 80 },
        2: { cellWidth: 25, halign: "right" },
        3: { cellWidth: 25, halign: "right" },
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
    });

    // Add validation message
    doc.setFontSize(10);
    doc.setTextColor(
      reportData.totalDebit === reportData.totalCredit
        ? [46, 125, 50]
        : [198, 40, 40]
    );
    doc.text(
      `Trial Balance ${
        reportData.totalDebit === reportData.totalCredit
          ? "Balances"
          : "Does Not Balance"
      }`,
      105,
      doc.lastAutoTable.finalY + 10,
      { align: "center" }
    );

    doc.save("Trial_Balance_Report.pdf");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Trial Balance</title>
          <style>
            body { font-family: Arial; margin: 0; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 18px; font-weight: bold; }
            .company-info { font-size: 12px; margin-bottom: 5px; }
            .report-title { font-size: 16px; font-weight: bold; margin: 10px 0; }
            .report-period { font-size: 12px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background-color: #2980b9; color: white; padding: 6px; text-align: left; }
            td { padding: 5px; border-bottom: 1px solid #ddd; }
            .text-right { text-align: right; }
            .total-row { font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; text-align: center; }
            .validation {
              margin-top: 15px;
              padding: 8px;
              text-align: center;
              font-weight: bold;
              border-radius: 4px;
            }
            .balanced {
              background-color: #e8f5e9;
              color: #2e7d32;
            }
            .not-balanced {
              background-color: #ffebee;
              color: #c62828;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">${
              companyData?.companyName || "Company"
            }</div>
            <div class="company-info">
              ${companyData?.address || ""}, ${companyData?.city || ""}<br>
              Phone: ${companyData?.phoneNumber || ""}
            </div>
            <div class="report-title">TRIAL BALANCE</div>
            <div class="report-period">
              As of ${moment(toDate).format("MMM DD, YYYY")}<br>
              Generated on: ${moment().format("MMM DD, YYYY hh:mm A")}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Account Code</th>
                <th>Account Name</th>
                <th class="text-right">Debit</th>
                <th class="text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.accounts
                .map(
                  (account) => `
                <tr>
                  <td>${account.accountCode}</td>
                  <td>${account.accountName}</td>
                  <td class="text-right">${
                    account.debit ? account.debit.toFixed(2) : "-"
                  }</td>
                  <td class="text-right">${
                    account.credit ? account.credit.toFixed(2) : "-"
                  }</td>
                </tr>
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="2">Totals</td>
                <td class="text-right">${reportData.totalDebit.toFixed(2)}</td>
                <td class="text-right">${reportData.totalCredit.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="validation ${
            reportData.totalDebit === reportData.totalCredit
              ? "balanced"
              : "not-balanced"
          }">
            Trial Balance ${
              reportData.totalDebit === reportData.totalCredit
                ? "Balances"
                : "Does Not Balance"
            }
          </div>

          <div class="footer">
            © ${new Date().getFullYear()} ${
      companyData?.companyName || "Company"
    }. All rights reserved.
          </div>

          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 200);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) return <p>Loading...</p>;
  if (!user) return <Login></Login>;

  return (
    <div id="layout-wrapper">
      <Header User={user} />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <AccontingReportTabs pageName="TRIALBALANCEREPORT" />
                </div>
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-end flex-wrap">
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">From Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => setFromDate(date)}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select from date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">To Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              minDate={fromDate}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select to date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="d-flex mb-2">
                          <Button
                            variant="primary"
                            className="me-2"
                            onClick={handleSearch}
                          >
                            <SearchOutlined /> Search
                          </Button>
                          <Button variant="danger" onClick={handleClear}>
                            <ReloadOutlined /> Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 d-flex justify-content-end">
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle variant="primary" id="dropdown-export">
                          <DownloadOutlined /> Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={exportToPDF}>
                            PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        variant="secondary"
                        onClick={handlePrint}
                        className="ms-2"
                      >
                        <PrinterOutlined /> Print
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="report-container">
                    <div className="header-section">
                      <h1 className="company-name">
                        {companyData?.companyName || "Company"}
                      </h1>
                      <p className="company-info">
                        {companyData?.address || ""}, {companyData?.city || ""}
                        <br />
                        Phone: {companyData?.phoneNumber || ""}
                      </p>
                    </div>

                    <div className="report-title">
                      <h2>Trial Balance</h2>
                      <p className="report-period">
                        As of {moment(toDate).format("MMM DD, YYYY")}
                      </p>
                    </div>

                    <Spin spinning={loadingReport}>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead className="bg-soft-primary bg-gradient">
                            <tr>
                              <th>Account Code</th>
                              <th>Account Name</th>
                              <th className="text-end">Debit</th>
                              <th className="text-end">Credit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.accounts.length > 0 ? (
                              reportData.accounts.map((account, idx) => (
                                <tr key={`account-${idx}`}>
                                  <td>{account.accountCode}</td>
                                  <td>{account.accountName}</td>
                                  <td className="text-end">
                                    {account.debit
                                      ? account.debit.toFixed(2)
                                      : "-"}
                                  </td>
                                  <td className="text-end">
                                    {account.credit
                                      ? account.credit.toFixed(2)
                                      : "-"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="text-center text-muted"
                                >
                                  No accounts found
                                </td>
                              </tr>
                            )}
                            <tr className="total-row">
                              <td colSpan="2">Totals</td>
                              <td className="text-end">
                                {reportData.totalDebit.toFixed(2)}
                              </td>
                              <td className="text-end">
                                {reportData.totalCredit.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Spin>

                    <div
                      className={`validation-section mt-3 p-2 text-center ${
                        reportData.totalDebit === reportData.totalCredit
                          ? "bg-soft-success"
                          : "bg-soft-danger"
                      }`}
                    >
                      <h5
                        className={`mb-0 ${
                          reportData.totalDebit === reportData.totalCredit
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        Trial Balance{" "}
                        {reportData.totalDebit === reportData.totalCredit
                          ? "Balances"
                          : "Does Not Balance"}
                      </h5>
                    </div>

                    <div className="report-footer mt-3">
                      <p className="mb-1">
                        This report was generated automatically on{" "}
                        {moment().format("MMM DD, YYYY")}.
                      </p>
                      <p className="mb-0">
                        © {new Date().getFullYear()}{" "}
                        {companyData?.companyName || "Company"}. All rights
                        reserved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceReport;
